import React, { useState, useEffect, useRef } from "react";
import { fetchCensus } from "../api";
import "./Dashboard.css";

const Logic = ({ children }) => (
  <p className="calc-logic"><b>How this is calculated:</b> {children}</p>
);

export default function Census({ ctx, passcode }) {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const retry = useRef(null);

  const load = (m) => {
    setLoading(true); setErr(null);
    fetchCensus(ctx, passcode, m)
      .then((r) => {
        if (r.warming) { setData({ warming: true }); retry.current = setTimeout(() => load(m), 10000); return; }
        setData(r); setMonth(r.month);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    return () => clearTimeout(retry.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err) return <div className="state error">Error: {err}</div>;
  if (data?.warming) {
    return (
      <section className="panel" style={{ textAlign: "center", padding: "36px 20px" }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>⏳</div>
        <h2>Loading census data…</h2>
        <p className="muted">The first load pulls shifts and call logs from Teambridge; this refreshes automatically.</p>
      </section>
    );
  }
  if (loading && !data) return <div className="state">Loading census…</div>;
  if (!data) return null;

  const { assessments: a, conversion: cv, lost: lo, hours: hr, referral: rf } = data;
  const labelFor = (m) => { const [y, mo] = m.split("-").map(Number); return new Date(Date.UTC(y, mo - 1, 1)).toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" }); };

  return (
    <div className={loading ? "dim" : ""}>
      {/* 1 — Assessments by month */}
      <section className="panel">
        <div className="panel-head"><h2>Health &amp; Wellness Assessments</h2><span>completed by month</span></div>
        <div className="pl-scroll">
          <table className="grid">
            <thead><tr><th>Month</th><th className="strong">Assessments</th></tr></thead>
            <tbody>
              {a.rows.map((r) => <tr key={r.month}><td className="rowhead">{r.label}</td><td className="strong">{r.count}</td></tr>)}
              <tr className="total-row"><td className="rowhead">12-month total</td><td className="strong">{a.total}</td></tr>
            </tbody>
          </table>
        </div>
        <Logic>{a.logic}</Logic>
      </section>

      {/* 4 — Caregiver billable hours weekly average */}
      <section className="panel">
        <div className="panel-head"><h2>Caregiver Billable Hours — Weekly Average</h2><span>hours worked ÷ weeks in month</span></div>
        <div className="pl-scroll">
          <table className="grid">
            <thead><tr><th>Month</th><th>Total hours</th><th className="strong">Weekly average</th></tr></thead>
            <tbody>
              {hr.rows.map((r) => <tr key={r.month}><td className="rowhead">{r.label}</td><td>{r.totalHours.toLocaleString()}</td><td className="strong">{r.weeklyAvg.toLocaleString()} h</td></tr>)}
            </tbody>
          </table>
        </div>
        <p className={"calc-trend " + (hr.decreased ? "neg-text" : "pos-text")}>
          Weekly average {hr.decreased ? "decreased" : "increased"} by {Math.abs(hr.delta).toLocaleString()} h
          from {hr.twoMonthsAgo.label} ({hr.twoMonthsAgo.weeklyAvg.toLocaleString()} h) to {hr.lastMonth.label} ({hr.lastMonth.weeklyAvg.toLocaleString()} h).
        </p>
        <Logic>{hr.logic}</Logic>
      </section>

      {/* Month filter for reports 2, 3, 5 */}
      <div className="month-filter">
        <span>Reports below — month:</span>
        <select className="month-select" value={month || data.month} onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}>
          {(data.months || []).map((m) => <option key={m} value={m}>{labelFor(m)}</option>)}
        </select>
      </div>

      {/* 3 — Clients lost */}
      <section className="panel">
        <div className="panel-head"><h2>Clients Lost — {lo.monthLabel}</h2><span>{lo.count} client{lo.count === 1 ? "" : "s"}</span></div>
        {lo.rows.length ? (
          <div className="pl-scroll">
            <table className="grid">
              <thead><tr><th>Client</th><th>Status</th><th>Probable reason</th><th>From call logs</th></tr></thead>
              <tbody>
                {lo.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="rowhead">{r.name}</td>
                    <td className="muted">{r.status}</td>
                    <td className="strong">{r.reason}</td>
                    <td className="muted" style={{ maxWidth: 360, whiteSpace: "normal", fontSize: 11.5 }}>{r.snippet || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="muted" style={{ padding: "4px 2px" }}>No clients lost this month.</p>}
        <Logic>{lo.logic}</Logic>
      </section>

      {/* 2 — Assessed → converted / not */}
      <section className="panel">
        <div className="panel-head"><h2>Assessed Clients — {cv.monthLabel}</h2><span>converted vs. not converted</span></div>
        {cv.rows.length ? (
          <div className="pl-scroll">
            <table className="grid">
              <thead><tr><th>Client</th><th>Converted</th><th>Not converted</th></tr></thead>
              <tbody>
                {cv.rows.map((r, i) => (
                  <tr key={i}><td className="rowhead">{r.name}</td><td className="pos-text">{r.converted ? "✓" : ""}</td><td className="neg-text">{r.converted ? "" : "✓"}</td></tr>
                ))}
                <tr className="total-row"><td className="rowhead">Subtotal</td><td className="strong">{cv.subtotals.converted}</td><td className="strong">{cv.subtotals.notConverted}</td></tr>
              </tbody>
            </table>
          </div>
        ) : <p className="muted" style={{ padding: "4px 2px" }}>No clients assessed this month.</p>}
        <Logic>{cv.logic}</Logic>
      </section>

      {/* 5 — Referral sources */}
      <section className="panel">
        <div className="panel-head"><h2>Clients by Referral Source — {rf.monthLabel}</h2><span>{rf.total} client{rf.total === 1 ? "" : "s"} · click a row to expand</span></div>
        <div className="pl-scroll">
          <table className="grid">
            <thead><tr><th>Referral source (standardized)</th><th className="strong">Clients</th></tr></thead>
            <tbody>
              {rf.rows.map((r, i) => (
                <React.Fragment key={i}>
                  <tr className="clickable" onClick={() => setExpanded((e) => ({ ...e, [r.source]: !e[r.source] }))}>
                    <td className="rowhead">{r.details.length ? (expanded[r.source] ? "▾ " : "▸ ") : ""}{r.source}</td>
                    <td className="strong">{r.count}</td>
                  </tr>
                  {expanded[r.source] && r.details.length > 0 && (
                    <tr><td colSpan={2} className="muted" style={{ whiteSpace: "normal", fontSize: 11.5, paddingLeft: 24 }}>{r.details.join(" · ")}</td></tr>
                  )}
                </React.Fragment>
              ))}
              <tr className="total-row"><td className="rowhead">Total</td><td className="strong">{rf.total}</td></tr>
            </tbody>
          </table>
        </div>
        <Logic>{rf.logic}</Logic>
      </section>

      <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>Live from Teambridge · as of {data.asOf}</p>
    </div>
  );
}
